import React, { PureComponent } from 'react';
import { st, classes, stVars, vars } from './Page.st.css';
import { PageContext } from './PageContext';
import { PageScrollContext } from './PageScrollContext';
import { PageHeader, HeaderProps } from './components/Header';
import { PageSection as Section, SectionProps } from './components/Section';
import { PageFooter as Footer } from './components/Footer';
import { Content } from './Content';
import { Tail } from './Tail';
import { PageSticky } from './PageSticky';
import { FixedFooter } from './FixedFooter';
import { ScrollableContainer } from '../common/ScrollableContainer';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import {
  ContentProps,
  PageProps,
  TailProps,
  FixedContentProps,
  FixedFooterProps,
} from './Page.types';

/*
 * Page structure without mini-header-overlay:
 *
 * + PageWrapper (Horizontal Scroll) --
 * | +- Page --------------------------
 * | | +--  ScrollableContainer (Vertical Scroll)
 * | | | +--  MinimizationPlaceholder
 * | | | |
 * | | | +-----------------------------
 * | | | +-- HeaderContainer ------ (position: fixed - when minimized)
 * | | | | +-- Page.Header ------------
 * | | | | |
 * | | | | +---------------------------
 * | | | | +-- Page.Tail --------------
 * | | | | |
 * | | | | +---------------------------
 * | | | +-----------------------------
 * | | | +-- ContentWrapper------------
 * | | | | +-- Page.FixedContent (Deprecated)
 * | | | | |
 * | | | | +---------------------------
 * | | | | +-- Page.Content -----------
 * | | | | | +-- Page.Section ---------
 * | | | | |
 * | | | | +---------------------------
 * | | | +-----------------------------
 * | | +-------------------------------
 * | +--------------------------------- (Page - End)
 * +----------------------------------- (PageWrapper - End)
 *
 * -  ScrollableContainer has a data-classnamed 'scrollable-content', and should NOT be renamed, since
 * Tooltip is hard-coded-ly using a selector like this: [data-class="page-scrollable-content"]
 */

type DefaultProps = Required<Pick<PageProps, 'maxWidth' | 'scrollProps'>>;
type Props = PageProps & DefaultProps;
type State = {
  headerContainerHeight: number;
  headerWrapperHeight: number;
  tailHeight: number;
  footerHeight: number;
  pageHeight?: number;
  windowHeight?: number;
  minimized: boolean;
};
type HeaderElement = React.ReactElement<HeaderProps>;
type SectionElement = React.ReactElement<SectionProps>;
type ContentElement = React.ReactElement<ContentProps>;
type FixedContentElement = React.ReactElement<FixedContentProps>;
type TailElement = React.ReactElement<TailProps>;
type FixedFooterElement = React.ReactElement<FixedFooterProps>;
type ChildrenObject = {
  PageHeader?: HeaderElement;
  Section?: SectionElement;
  PageContent?: ContentElement;
  PageFixedContent?: FixedContentElement;
  PageTail?: TailElement;
  FixedFooter?: FixedFooterElement;
};

const FixedContent = (props: { children: React.ReactNode }) => props.children;
FixedContent.displayName = 'Page.FixedContent';
class Page extends PureComponent<Props, State> {
  static displayName = 'Page';
  static contextType = WixDesignSystemContext;
  static defaultProps: DefaultProps = {
    maxWidth: parseInt(stVars.mainContainerMaxWidth, 10),
    scrollProps: {},
  };
  static Header = PageHeader;
  static Section = Section;
  static Content = Content;
  static FixedContent = FixedContent; // TODO: deprecate, use Page.Sticky instead
  static Tail = Tail;
  static FixedFooter = FixedFooter;
  static Sticky = PageSticky;
  static Footer = Footer;

  scrollableContainerRef: React.RefObject<HTMLDivElement>;
  headerWrapperRef: HTMLDivElement | null;
  headerContainerRef: HTMLDivElement | null;
  pageHeaderTailRef: HTMLDivElement | null;
  pageRef: HTMLDivElement | null;
  footerWrapperRef: HTMLDivElement | null;
  pageScrollContextValue: {
    scrollableContentRef: React.RefObject<HTMLDivElement>;
  };

  headerResizeObserver: ResizeObserver | undefined;

  constructor(props: Props) {
    super(props);

    this.scrollableContainerRef = React.createRef();
    this.headerWrapperRef = null;
    this.headerContainerRef = null;
    this.pageHeaderTailRef = null;
    this.pageRef = null;
    this.footerWrapperRef = null;
    this.pageScrollContextValue = {
      scrollableContentRef: this.scrollableContainerRef,
    };

    this._handleScroll = this._handleScroll.bind(this);
    this._handleWindowResize = this._handleWindowResize.bind(this);
    this._calculateComponentsHeights =
      this._calculateComponentsHeights.bind(this);

    this.state = {
      headerContainerHeight: 0,
      headerWrapperHeight: 0,
      tailHeight: 0,
      footerHeight: 0,
      minimized: false,
    };
  }

  componentDidMount() {
    this._calculateComponentsHeights();
    window.addEventListener('resize', this._handleWindowResize);

    if (this.headerContainerRef && typeof ResizeObserver !== 'undefined') {
      this.headerResizeObserver = new ResizeObserver(
        this._calculateComponentsHeights,
      );
      this.headerResizeObserver.observe(this.headerContainerRef);
    }

    // This is done for backward compatibility only,
    // Notifying current users that passed the `scrollableContentRef` prop about the ref current value.
    // New users should be encouraged to use the new event handlers onScrollChanged/onScrollAreaChanged
    // according to their use case.
    this.props.scrollableContentRef &&
      this.props.scrollableContentRef(this.scrollableContainerRef.current);
  }

  componentDidUpdate() {
    this._calculateComponentsHeights();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._handleWindowResize);
    this.headerResizeObserver?.disconnect();
  }

  _getNamedChildren() {
    return getChildrenObject(this.props.children);
  }

  _calculateComponentsHeights() {
    const {
      headerContainerHeight,
      headerWrapperHeight,
      tailHeight,
      pageHeight,
      footerHeight,
      minimized,
    } = this.state;

    const newHeaderWrapperHeight =
      this.headerWrapperRef && !minimized
        ? this.headerWrapperRef.getBoundingClientRect().height
        : headerWrapperHeight;

    const newHeaderContainerHeight =
      this.headerWrapperRef && !minimized && this.headerContainerRef
        ? this.headerContainerRef.getBoundingClientRect().height
        : headerContainerHeight;

    const newTailHeight = this.pageHeaderTailRef
      ? this.pageHeaderTailRef.offsetHeight
      : 0;
    const newPageHeight = this.pageRef ? this.pageRef.offsetHeight : 0;
    const newFooterHeight = this.footerWrapperRef
      ? this.footerWrapperRef.offsetHeight
      : 0;

    if (
      headerContainerHeight !== newHeaderContainerHeight ||
      headerWrapperHeight !== newHeaderWrapperHeight ||
      tailHeight !== newTailHeight ||
      pageHeight !== newPageHeight ||
      footerHeight !== newFooterHeight
    ) {
      this.setState({
        headerContainerHeight: newHeaderContainerHeight,
        headerWrapperHeight: newHeaderWrapperHeight,
        tailHeight: newTailHeight,
        pageHeight: newPageHeight,
        footerHeight: newFooterHeight,
      });
    }
  }

  _getScrollContainer() {
    return this.scrollableContainerRef.current;
  }

  _getMinimizedHeaderWrapperHeight() {
    if (!this._hasHeader()) {
      return 0;
    }

    return this._hasTail()
      ? parseInt(stVars.minimizedHeaderWrapperWithTailHeightPx, 10)
      : parseInt(stVars.minimizedHeaderWrapperHeightPx, 10);
  }

  _getMinimizationDiff() {
    const { headerWrapperHeight } = this.state;
    return headerWrapperHeight
      ? headerWrapperHeight - this._getMinimizedHeaderWrapperHeight()
      : null;
  }

  _handleScroll(e: HTMLElement) {
    const containerScrollTop = this._getScrollContainer()?.scrollTop ?? 0;

    const { minimized } = this.state;

    const minimizationDiff = this._getMinimizationDiff();
    const nextDisplayMiniHeader = !!(
      minimizationDiff && containerScrollTop >= minimizationDiff
    );

    if (minimized !== nextDisplayMiniHeader) {
      this.setState({
        minimized: nextDisplayMiniHeader,
      });
    }

    const onScrollChanged = this.props.scrollProps?.onScrollChanged;
    if (onScrollChanged) {
      onScrollChanged(e);
    }
  }

  _handleWindowResize() {
    // TODO: Optimize : https://developer.mozilla.org/en-US/docs/Web/Events/resize

    // Taken from here: https://github.com/kunokdev/react-window-size-listener/blob/d64c077fba4d4e0ce060464078c5fc19620528e6/src/index.js#L66
    const windowHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;

    if (this.state.windowHeight !== windowHeight) {
      // We are not using windowHeight directly, since we need to measure the `<Page/>`'s height,
      // But we hold it in the state to avoid rendering when only window.width changes
      this.setState({ windowHeight });
    }
  }

  _safeGetChildren(element?: ContentElement) {
    if (!element || !element.props || !element.props.children) {
      return [];
    }

    return element.props.children;
  }

  _getPageDimensionsStyle(): React.CSSProperties | undefined {
    const { maxWidth, sidePadding } = this.props;
    // TODO: Simplify - maxWidth is always truthy (from defaultProp)
    if (!maxWidth && !sidePadding && sidePadding !== 0) {
      return undefined;
    }

    const styles: React.CSSProperties = {};
    if (maxWidth) {
      styles.maxWidth = `${maxWidth}px`;
    }

    if (sidePadding || sidePadding === 0) {
      styles.paddingLeft = `${sidePadding}px`;
      styles.paddingRight = `${sidePadding}px`;
    }

    return styles;
  }

  _hasBackgroundImage() {
    return !!this.props.backgroundImageUrl;
  }

  _hasGradientClassName() {
    return !!this.props.gradientClassName && !this.props.backgroundImageUrl;
  }

  _renderContentHorizontalLayout(
    props: React.HTMLAttributes<HTMLDivElement> & {
      horizontalScroll?: boolean;
    },
  ) {
    const { PageContent } = this._getNamedChildren();
    const contentFullScreen = PageContent && PageContent.props.fullScreen;

    const { className, horizontalScroll, style, ...rest } = props;

    const pageDimensionsStyle = contentFullScreen
      ? null
      : this._getPageDimensionsStyle();

    return (
      <div
        className={st(
          classes.contentHorizontalLayout,
          {
            contentFullWidth: contentFullScreen,
            horizontalScroll,
          },
          className,
        )}
        style={{ ...pageDimensionsStyle, ...style }}
        {...rest}
      >
        {props.children}
      </div>
    );
  }

  _renderHeader() {
    const { minimized } = this.state;
    const { PageHeader: PageHeaderChild } = this._getNamedChildren();
    const dataHook = 'page-header-wrapper';

    return (
      PageHeaderChild && (
        <div
          key={dataHook}
          data-hook={dataHook}
          className={st(classes.headerWrapper, {
            minimized,
          })}
          ref={ref => {
            this.headerWrapperRef = ref;
          }}
        >
          {React.cloneElement(PageHeaderChild, {
            minimized,
            hasBackgroundImage: this._hasBackgroundImage(),
          })}
        </div>
      )
    );
  }

  _renderHeaderContainer() {
    const { minimized } = this.state;

    // placeholder when header is minimized
    const placeholder = (
      <div
        key="placeholder"
        style={{
          height: `${minimized ? this._getMinimizationDiff() : 0}px`,
        }}
      />
    );

    /**
     * HeaderContainer has position sticky. The `top` value is negative, in order to let
     * the container scroll out of view before the minimization occurs.
     */
    const top = minimized ? `-${this._getMinimizationDiff()}px` : 0;

    return (
      <div
        data-hook="page-header-container"
        className={st(classes.pageHeaderContainer, {
          minimized,
          hasTail: this._hasTail(),
        })}
        style={{
          [vars.minimizationTop]: top,
        }}
        ref={ref => {
          this.headerContainerRef = ref;
        }}
      >
        {this._renderContentHorizontalLayout({
          children: [placeholder, this._renderHeader(), this._renderTail()],
        })}
      </div>
    );
  }

  _renderScrollableContainer() {
    const onScrollAreaChanged = this.props.scrollProps?.onScrollAreaChanged;
    return (
      <ScrollableContainer
        className={st(classes.scrollableContainer, {
          hasTail: this._hasTail(),
        })}
        dataHook="page-scrollable-content"
        data-class="page-scrollable-content"
        ref={this.scrollableContainerRef}
        onScrollAreaChanged={onScrollAreaChanged}
        onScrollChanged={this._handleScroll}
      >
        <div data-hook="safari-12-13-sticky-fix">
          {this._renderScrollableBackground()}

          {this._renderHeaderContainer()}
          {this._renderContentContainer()}
          {this._renderFixedFooter()}
        </div>
      </ScrollableContainer>
    );
  }

  _hasTail() {
    return !!this._getNamedChildren().PageTail;
  }

  _hasHeader() {
    return !!this._getNamedChildren().PageHeader;
  }

  _renderScrollableBackground() {
    const { headerContainerHeight, tailHeight } = this.state;

    const backgroundHeight = `${
      headerContainerHeight -
      tailHeight +
      (this._hasTail() ? 0 : parseInt(stVars.backgroundCoverContentPx, 10))
    }px`;

    if (this._hasBackgroundImage()) {
      return (
        <div
          className={classes.imageBackgroundContainer}
          style={{ height: backgroundHeight }}
          data-hook="page-background-image"
        >
          <div
            className={classes.imageBackground}
            style={{ backgroundImage: `url(${this.props.backgroundImageUrl})` }}
          />
        </div>
      );
    }

    if (this._hasGradientClassName()) {
      return (
        <div
          data-hook="page-gradient-class-name"
          className={st(
            classes.gradientBackground,
            {},
            this.props.gradientClassName,
          )}
          style={{ height: backgroundHeight }}
        />
      );
    }

    return null;
  }

  _renderTail() {
    const { PageTail } = this._getNamedChildren();
    const dataHook = 'page-tail';

    return (
      PageTail && (
        <div
          data-hook={dataHook}
          key={dataHook}
          className={classes.tail}
          ref={r => {
            this.pageHeaderTailRef = r;
          }}
        >
          {PageTail}
        </div>
      )
    );
  }

  _renderContentContainer() {
    const { footerHeight } = this.state;
    const { children } = this.props;
    const childrenObject = getChildrenObject(children);
    const { PageContent, PageFixedContent } = childrenObject;

    return (
      <PageContext.Provider
        value={{
          stickyStyle: {
            top: `${
              this._getMinimizedHeaderWrapperHeight() + this.state.tailHeight
            }px`,
          },
          scrollableContentRef: this.scrollableContainerRef,
        }}
      >
        <PageScrollContext.Provider value={this.pageScrollContextValue}>
          {this._renderContentHorizontalLayout({
            className: classes.contentContainer,
            style: {
              paddingBottom: footerHeight || '48px',
            },
            horizontalScroll: this.props.horizontalScroll,
            children: (
              <div className={classes.contentFloating}>
                {PageFixedContent && (
                  <PageSticky data-hook="page-fixed-content">
                    {React.cloneElement(PageFixedContent)}
                  </PageSticky>
                )}
                {this._safeGetChildren(PageContent)}
              </div>
            ),
          })}
        </PageScrollContext.Provider>
      </PageContext.Provider>
    );
  }

  _renderFixedFooter = () => {
    const { children } = this.props;
    const childrenObject = getChildrenObject(children);
    const { FixedFooter: FixedFooterChild, PageContent } = childrenObject;
    const contentFullScreen = PageContent && PageContent.props.fullScreen;

    const pageDimensionsStyle = contentFullScreen
      ? undefined
      : this._getPageDimensionsStyle();

    if (FixedFooterChild) {
      return (
        <div
          className={classes.fixedFooter}
          ref={ref => {
            this.footerWrapperRef = ref;
          }}
          style={pageDimensionsStyle}
        >
          {React.cloneElement(FixedFooterChild, {})}
        </div>
      );
    }

    return null;
  };

  render() {
    const { dataHook, className, minWidth, zIndex, height } = this.props;
    const { mobile } = this.context as React.ContextType<
      typeof WixDesignSystemContext
    >;
    const pageMinWidth =
      minWidth ??
      (mobile ? undefined : parseInt(stVars.mainContainerMinWidth, 10));
    const pageSidePadding = mobile
      ? stVars.pageSidePaddingSmall
      : stVars.pageSidePadding;

    return (
      <div
        data-hook={dataHook}
        className={st(classes.root, {}, className)}
        style={{ zIndex, height }}
      >
        <div
          data-hook="page"
          className={classes.page}
          style={
            pageMinWidth === undefined
              ? undefined
              : {
                  minWidth: `calc(${pageMinWidth}px + 2 * ${pageSidePadding})`,
                }
          }
          ref={ref => {
            this.pageRef = ref;
          }}
        >
          {this._renderScrollableContainer()}
        </div>
      </div>
    );
  }

  /**
   * Scrolls the page to a particular set of coordinates
   * @param {ScrollToOptions} scrollTo { left: number, top: number, behavior: 'smooth' | 'auto' }
   */
  scrollTo(scrollTo?: ScrollToOptions) {
    const scrollContainer = this._getScrollContainer();
    scrollContainer?.scrollTo(scrollTo);
  }
}

function getChildrenObject(children?: React.ReactNode): ChildrenObject {
  return React.Children.toArray(children).reduce(
    (acc: ChildrenObject, child) => {
      if (!React.isValidElement(child)) {
        return acc;
      }

      const displayName =
        typeof child.type === 'string' || typeof child.type === 'symbol'
          ? undefined
          : (child.type as React.ComponentType).displayName;

      switch (displayName) {
        case 'Page.Header': {
          acc.PageHeader = child as HeaderElement;
          break;
        }
        case 'Page.Section': {
          acc.Section = child as SectionElement;
          break;
        }
        case 'Page.Content': {
          acc.PageContent = child as ContentElement;
          break;
        }
        case 'Page.FixedContent': {
          acc.PageFixedContent = child as FixedContentElement;
          break;
        }
        case 'Page.Tail': {
          acc.PageTail = child as TailElement;
          break;
        }
        case 'Page.FixedFooter': {
          acc.FixedFooter = child as FixedFooterElement;
          break;
        }
        default: {
          break;
        }
      }
      return acc;
    },
    {},
  );
}

export { Page };
